"use client";

import React, { useEffect, useRef } from "react";

interface Logo {
  name: string;
  src: string;
  alt: string;
}

const LogoSlider = () => {
  const sliderRef = useRef<HTMLDivElement>(null);

  // Company logos data
  const logos: Logo[] = [
    {
      name: "TOTVS",
      src: "/images/logos/logo-totvs.svg",
      alt: "SERTAC Logo",
    },
    {
      name: "TIM",
      src: "/images/logos/tim-logo-1.svg",
      alt: "TIM Logo",
    },
    {
      name: "Senac",
      src: "/images/logos/site_senac.png",
      alt: "Senac Logo",
    },
    {
      name: "lance!",
      src: "/images/logos/lance-logo.svg",
      alt: "O+ Logo",
    },
  ];

  useEffect(() => {
    if (!sliderRef.current) return;

    const slider = sliderRef.current;

    // Use CSS animation for perfect infinite loop
    slider.style.animation = "slide 20s linear infinite";

    // Pause on hover using CSS
    const handleMouseEnter = () => {
      slider.style.animationPlayState = "paused";
    };

    const handleMouseLeave = () => {
      slider.style.animationPlayState = "running";
    };

    slider.addEventListener("mouseenter", handleMouseEnter);
    slider.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      slider.removeEventListener("mouseenter", handleMouseEnter);
      slider.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  // Create double sets for seamless loop
  const logoSets = Array(2).fill(logos).flat();

  return (
    <div className="pt-8">
      <div
        className="overflow-hidden"
        style={{
          maskImage:
            "linear-gradient(90deg, transparent 0%, black 15%, black 85%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(90deg, transparent 0%, black 15%, black 85%, transparent 100%)",
        }}
      >
        <div
          ref={sliderRef}
          className="flex items-center gap-8"
          style={{
            width: "fit-content",
            willChange: "transform",
            backfaceVisibility: "hidden",
            perspective: "1000px",
          }}
        >
          {logoSets.map((logo, index) => (
            <div
              key={`${logo.name}-${index}`}
              className="flex-shrink-0 opacity-60 hover:opacity-80 transition-opacity duration-300"
              style={{
                width: "112px",
                transform: "translateZ(0)", // Force hardware acceleration
                willChange: "opacity",
              }}
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="h-8 w-auto object-contain filter brightness-0 invert opacity-60"
                style={{
                  transform: "translateZ(0)",
                }}
                width={112}
                height={32}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  // Fallback to text if image fails to load
                  const target = e.target as HTMLImageElement;
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = `<div class="text-white/60 text-lg font-semibold">${logo.name}</div>`;
                  }
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LogoSlider;
